// src/contexts/OperacaoLoteContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiGet } from '../services/api';
import { showAlert } from './DialogContext';
import { logger } from '@/utils/logger';
import { isStatusTerminal, type OperacaoLoteResponse } from '@/types/lote/OperacaoLote';
import { mensagemResultado } from '@/utils/selectAll';

/**
 * Acompanhamento das operações em lote do backend (issue #56 / ADR-037), no
 * nível da aplicação.
 *
 * POR QUE AQUI E NÃO NA TELA. Quando uma ação responde 202, o trabalho segue no
 * servidor e o usuário está livre para sair — é a razão de o 202 existir. Com o
 * acompanhamento dentro do painel, fechar o painel matava o polling (o aviso de
 * conclusão nunca chegava) e zerava a trava anti-reentrada (reabrir devolvia os
 * botões, permitindo disparar um segundo lote sobre o mesmo pai).
 *
 * Segue o esqueleto de ToastContext / BusyContext / DialogContext: contexto +
 * estado + registro em App.tsx. Sem ponte singleton: aqui todos os chamadores
 * são componentes React, e a ponte só existe naqueles para atender callers de
 * fora da árvore (o interceptor do axios).
 *
 * ── TRÊS RESTRIÇÕES DE DESENHO ──────────────────────────────────────────────
 * Elas são o que faz este provider servir às 9 superfícies do #56 sem virar um
 * `switch` de nove ramos. Não afrouxar nenhuma sem revisar as outras.
 *
 * 1. A CHAVE É STRING OPACA. Espelha o par `(acao, chave_pai)` do backend, cujo
 *    `chave_pai` é VARCHAR — em CRIAR_REQUISICAO ele é a chave composta
 *    (nroContato, codFilial) serializada, não um id. Este arquivo compara a
 *    chave e NUNCA a interpreta, exatamente como o backend faz.
 *
 * 2. SÓ O RAMO ASSÍNCRONO SOBE. O caminho inline (200/201) fica na tela, porque
 *    o corpo da resposta é específico de cada superfície — array de decisões por
 *    item na aprovação, id da conferência na criação. Limitado ao 202, este
 *    arquivo conhece UM formato: OperacaoLoteResponse.
 *
 * 3. COLEÇÃO, NÃO SLOT ÚNICO. O backend permite lotes simultâneos em pais
 *    diferentes (a trava é por par). Um slot só quebraria na segunda superfície.
 * ────────────────────────────────────────────────────────────────────────────
 */

/** Primeiro intervalo de polling. */
const POLL_INICIAL_MS = 1_500;
/** Fator de backoff — evita martelar o backend numa operação longa. */
const POLL_FATOR = 1.5;
/** Teto do intervalo. */
const POLL_MAX_MS = 10_000;
/**
 * Quantas falhas de TRANSPORTE seguidas o acompanhamento tolera antes de
 * desistir. Só transporte: resposta do servidor não melhora com repetição — ver
 * `ehFalhaDeTransporte`.
 */
const POLL_MAX_FALHAS = 3;

/**
 * Vale a pena tentar de novo?
 *
 * Sim para rede caída, timeout e 5xx — o trabalho segue no servidor e o problema
 * é o caminho até ele. Não para 4xx: a linha do lote NUNCA é expurgada (não há
 * job de limpeza no backend) e o id veio do nosso próprio 202, então um 400
 * depois de um 202 aceito é escopo ou id errado, não algo transitório. Insistir
 * só adiaria o aviso.
 *
 * Os dois 400 possíveis aqui — "não encontrada" e erro de escopo — chegam com o
 * mesmo formato de corpo, então a decisão NÃO depende de ler a mensagem.
 */
const ehFalhaDeTransporte = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === undefined || status >= 500;
};

interface OperacaoLoteContextValue {
  /**
   * Registra um lote aceito (202) e passa a acompanhá-lo até o estado terminal,
   * avisando o usuário no início e no fim.
   */
  acompanhar: (chave: string, lote: OperacaoLoteResponse) => void;
  /** true enquanto houver lote em voo para a chave. Espelha a trava do backend. */
  estaEmAndamento: (chave: string) => boolean;
  /**
   * Assina a conclusão de uma chave; devolve a função de cancelamento.
   * A tela usa isso para recarregar a própria lista — se ainda estiver montada.
   * Se ninguém estiver ouvindo, nada acontece: a próxima abertura já busca do
   * servidor.
   */
  aoConcluir: (chave: string, callback: (lote: OperacaoLoteResponse) => void) => () => void;
}

const OperacaoLoteContext = createContext<OperacaoLoteContextValue | undefined>(undefined);

export const OperacaoLoteProvider = ({ children }: { children: ReactNode }) => {
  // Restrição 3: um mapa, não um slot. Em estado (e não em ref) porque
  // `estaEmAndamento` precisa re-renderizar os botões das telas.
  const [emAndamento, setEmAndamento] = useState<Record<string, OperacaoLoteResponse>>({});

  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const inscritosRef = useRef<Map<string, Set<(lote: OperacaoLoteResponse) => void>>>(new Map());
  const montadoRef = useRef(true);

  useEffect(() => {
    const timers = timersRef.current;
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const aoConcluir = useCallback(
    (chave: string, callback: (lote: OperacaoLoteResponse) => void) => {
      const mapa = inscritosRef.current;
      if (!mapa.has(chave)) mapa.set(chave, new Set());
      mapa.get(chave)!.add(callback);
      return () => {
        const set = mapa.get(chave);
        if (!set) return;
        set.delete(callback);
        if (set.size === 0) mapa.delete(chave);
      };
    },
    [],
  );

  const encerrar = useCallback((chave: string, lote: OperacaoLoteResponse) => {
    timersRef.current.delete(chave);
    setEmAndamento((prev) => {
      const next = { ...prev };
      delete next[chave];
      return next;
    });

    // Avisa quem estiver ouvindo ANTES do modal: a lista já se atualiza enquanto
    // o usuário lê a mensagem.
    inscritosRef.current.get(chave)?.forEach((cb) => {
      try {
        cb(lote);
      } catch (err) {
        logger.error('Erro em inscrito de operação em lote:', err);
      }
    });

    const msg = mensagemResultado({
      assincrono: true,
      status: lote.status,
      totalProcessado: lote.totalProcessado,
      totalIgnorado: lote.totalIgnorado,
      erroMensagem: lote.erroMensagem,
    });
    // Modal com OK, e não toast, de propósito: o usuário pode estar em outra
    // tela e no meio de outra coisa. Um toast some sozinho — e o que foi
    // prometido a ele no aceite foi "avisaremos quando terminar".
    void showAlert(msg.detalhe ? `${msg.texto}\n${msg.detalhe}` : msg.texto, {
      variant: msg.variante,
      title: msg.variante === 'error' ? 'Falha no processamento' : 'Processamento concluído',
    });
  }, []);

  const consultar = useCallback(
    async (chave: string, loteId: number, intervalo: number, falhas = 0) => {
      if (!montadoRef.current) return;
      try {
        // `silent: true`: leitura de fundo. Sem isso o Busy Overlay bloquearia a
        // tela inteira num trabalho que o usuário já foi avisado que continua
        // em segundo plano.
        const res = await apiGet(`/v1/operacoes-lote/${loteId}`, { silent: true });
        if (!montadoRef.current) return;
        const lote = res.data as OperacaoLoteResponse;

        if (isStatusTerminal(lote.status)) {
          encerrar(chave, lote);
          return;
        }

        setEmAndamento((prev) => ({ ...prev, [chave]: lote }));
        const proximo = Math.min(intervalo * POLL_FATOR, POLL_MAX_MS);
        timersRef.current.set(
          chave,
          // Consulta boa zera o contador: o orçamento é de falhas SEGUIDAS.
          setTimeout(() => void consultar(chave, loteId, proximo, 0), proximo),
        );
      } catch (err) {
        // Falha de polling não invalida o trabalho: ele segue no servidor. Solta
        // a trava para o usuário não ficar preso e avisa que o acompanhamento
        // caiu — sem afirmar que a operação falhou, porque não sabemos.
        logger.error('Erro ao consultar operação em lote:', err);
        if (!montadoRef.current) return;

        // Um blip de rede não vale desistir de acompanhar. Com o limiar do
        // backend baixo, o polling é caminho comum, e cada 5xx isolado viraria um
        // aviso de acompanhamento perdido num lote que terminou bem.
        if (ehFalhaDeTransporte(err) && falhas + 1 < POLL_MAX_FALHAS) {
          const proximo = Math.min(intervalo * POLL_FATOR, POLL_MAX_MS);
          timersRef.current.set(
            chave,
            setTimeout(() => void consultar(chave, loteId, proximo, falhas + 1), proximo),
          );
          return;
        }

        timersRef.current.delete(chave);
        setEmAndamento((prev) => {
          const next = { ...prev };
          delete next[chave];
          return next;
        });
        void showAlert(
          'Não foi possível acompanhar o processamento. A operação continua no servidor; confira a lista em instantes.',
          { variant: 'warning', title: 'Acompanhamento interrompido' },
        );
      }
    },
    [encerrar],
  );

  const acompanhar = useCallback(
    (chave: string, lote: OperacaoLoteResponse) => {
      setEmAndamento((prev) => ({ ...prev, [chave]: lote }));
      void showAlert(
        'A ação foi aceita e está sendo processada. Avisaremos quando terminar — você pode continuar usando o sistema.',
        { variant: 'info', title: 'Processando em segundo plano' },
      );
      timersRef.current.set(
        chave,
        setTimeout(() => void consultar(chave, lote.id, POLL_INICIAL_MS), POLL_INICIAL_MS),
      );
    },
    [consultar],
  );

  const estaEmAndamento = useCallback(
    (chave: string) => emAndamento[chave] !== undefined,
    [emAndamento],
  );

  const value = useMemo<OperacaoLoteContextValue>(
    () => ({ acompanhar, estaEmAndamento, aoConcluir }),
    [acompanhar, estaEmAndamento, aoConcluir],
  );

  return (
    <OperacaoLoteContext.Provider value={value}>{children}</OperacaoLoteContext.Provider>
  );
};

export const useOperacoesLote = (): OperacaoLoteContextValue => {
  const ctx = useContext(OperacaoLoteContext);
  if (!ctx) {
    throw new Error('useOperacoesLote deve ser usado dentro de um OperacaoLoteProvider');
  }
  return ctx;
};

/**
 * Monta a chave da trava. Formato `<ACAO>:<pai>`, espelhando o par
 * `(acao, chave_pai)` do backend. O segundo componente é texto livre — ver a
 * restrição 1 no topo do arquivo.
 */
export const chaveLote = (acao: string, pai: string | number): string => `${acao}:${pai}`;

// NOTA SOBRE LOGOUT: um lote em voo no momento do logout não precisa de
// tratamento próprio. O interceptor de 401 do api.ts faz `window.location.href`
// (api.ts, tratamento de sessão expirada), que recarrega a página e destrói
// todos os timers junto. Não vale acoplar este provider ao AuthContext por isso.
//
// LIMITE CONHECIDO: um F5 no meio do processamento perde o aviso — o id do lote
// vive na memória da página. A operação conclui no servidor normalmente. Tornar
// isso persistente é item separado, fora do escopo da issue #31.
